import { buildCompositeKey } from "./compositeKey";

/**
 * Discovery only runs on small mappings for now — beyond this, callers skip
 * the run entirely and leave any existing unique indexes untouched.
 */
export const MAX_DISCOVERY_ROWS = 1000;

/**
 * A single row proves nothing — every column is vacuously unique — so
 * discovery of new tuples needs at least this many rows. Existing tuples are
 * still re-verified (they hold vacuously).
 */
export const MIN_DISCOVERY_ROWS = 2;

/** Largest column combination the levelwise search will try. */
export const MAX_DISCOVERY_TUPLE_SIZE = 4;

/**
 * Most columns the levelwise search will consider. A mapping schema can hold
 * up to 100 columns, and when few of them are unique, apriori pruning removes
 * almost nothing — level 4 over ~96 columns is millions of combinations, each
 * scanning up to `MAX_DISCOVERY_ROWS` rows, all on the event loop. Capping at
 * 20 bounds the whole search at C(20,4) ≈ 4.8k combinations. The highest-
 * cardinality columns are kept — they are the likeliest key parts.
 */
export const MAX_DISCOVERY_COLUMNS = 20;

export type SkippedColumnReason =
  "key-column" | "has-nulls" | "constant" | "over-column-cap";

export interface SkippedColumn {
  column: string;
  reason: SkippedColumnReason;
}

export interface UniqueIndexDiscoveryResult {
  /**
   * Verified unique indexes: existing tuples the data still supports, plus
   * newly discovered minimal candidate keys (columns in schema order).
   */
  tuples: string[][];
  /** Existing tuples the current data contradicts (or that name unknown columns). */
  droppedTuples: string[][];
  skippedColumns: SkippedColumn[];
}

/**
 * Unique index discovery: finds the minimal column combinations (size 1 to
 * `MAX_DISCOVERY_TUPLE_SIZE`) whose values uniquely identify every row.
 *
 * Levelwise with apriori pruning — a combination containing an already-unique
 * tuple can never be a *minimal* key, so it is never tested. The declared key
 * columns are excluded from the search outright (that set is certain-unique by
 * construction), as are columns with nulls (a null key part makes a row
 * unreachable by lookup — see `projectFrameRows`/`lookupByKeys`) and constant
 * columns (they can never distinguish rows).
 *
 * Uniqueness is judged on `buildCompositeKey` of the stringified values — the
 * exact encoding composite-key lookup uses — so a tuple reported unique here
 * is unique for lookup purposes too.
 *
 * `existingTuples` (manual or previously discovered) are re-verified against
 * the rows: those that still hold are kept and treated as known-unique for
 * pruning; those the data contradicts are dropped.
 */
// The levelwise search is one algorithm (verify, prune, level 1, levels 2..n);
// splitting it would spread the knownUnique invariant across functions.
// Unchanged in this PR, which only moved it here from
// backend/src/core/services/data-mappings/.
// fallow-ignore-next-line complexity
export function discoverUniqueIndexes(args: {
  /** Column (field) names, in schema order — one per row cell. */
  columns: readonly string[];
  rows: ReadonlyArray<readonly unknown[]>;
  /** The table's declared composite key — excluded from the search. */
  keyColumns: readonly string[];
  existingTuples?: ReadonlyArray<readonly string[]>;
  /**
   * Tuples whose uniqueness is guaranteed elsewhere (physically enforced
   * indexes). Seeded as known-unique so the search never re-derives or
   * extends them, but never emitted — they are not this run's to manage.
   */
  guaranteedTuples?: ReadonlyArray<readonly string[]>;
  maxTupleSize?: number;
}): UniqueIndexDiscoveryResult {
  const maxTupleSize = args.maxTupleSize ?? MAX_DISCOVERY_TUPLE_SIZE;
  // Map, not a plain object: column names are user data, and one literally
  // named "__proto__" must still get its own entry.
  const columnIndex = new Map<string, number>();
  args.columns.forEach((column, index) => columnIndex.set(column, index));

  const isUnique = (indexes: readonly number[]): boolean =>
    tupleIsUnique(args.rows, indexes);

  // Re-verify what's already declared before searching for anything new.
  const survivors: string[][] = [];
  const droppedTuples: string[][] = [];
  const survivorKeys = new Set<string>();
  for (const tuple of args.existingTuples ?? []) {
    const indexes = tuple.map((column) => columnIndex.get(column));
    const holds =
      indexes.every((index) => index !== undefined) &&
      isUnique(indexes as number[]);
    if (holds) {
      survivors.push([...tuple]);
      survivorKeys.add(tupleKey(tuple));
    } else {
      droppedTuples.push([...tuple]);
    }
  }

  const { pool, skippedColumns } = buildSearchPool(args);
  if (args.rows.length < MIN_DISCOVERY_ROWS) {
    return { tuples: survivors, droppedTuples, skippedColumns };
  }

  // Known-unique column sets, used for apriori pruning. Seeded with the
  // survivors so the search never re-derives (or extends) a declared index,
  // and with the guaranteed (enforced) tuples, which need no verification.
  const knownUnique: Set<string>[] = survivors.map((tuple) => new Set(tuple));
  for (const tuple of args.guaranteedTuples ?? []) {
    knownUnique.push(new Set(tuple));
  }
  const discovered: string[][] = [];

  // Level 1: unique single columns, which then leave the pool — any
  // combination containing one could never be minimal.
  let survivorsPool: string[] = [];
  for (const column of pool) {
    if (containsKnownUnique(knownUnique, [column])) continue;
    if (isUnique([columnIndex.get(column) as number])) {
      discovered.push([column]);
      knownUnique.push(new Set([column]));
    } else {
      survivorsPool.push(column);
    }
  }

  // Levels 2..max: combinations of the remaining non-unique columns.
  for (let size = 2; size <= maxTupleSize; size += 1) {
    if (survivorsPool.length < size) break;
    for (const combo of combinations(survivorsPool, size)) {
      if (containsKnownUnique(knownUnique, combo)) continue;
      const indexes = combo.map((column) => columnIndex.get(column) as number);
      if (isUnique(indexes)) {
        discovered.push(combo);
        knownUnique.push(new Set(combo));
      }
    }
  }

  const fresh = discovered.filter(
    (tuple) => !survivorKeys.has(tupleKey(tuple)),
  );
  return { tuples: [...survivors, ...fresh], droppedTuples, skippedColumns };
}

/**
 * Columns eligible for the search, in schema order, with the reason each
 * ineligible column was set aside.
 */
function buildSearchPool(args: {
  columns: readonly string[];
  rows: ReadonlyArray<readonly unknown[]>;
  keyColumns: readonly string[];
}): { pool: string[]; skippedColumns: SkippedColumn[] } {
  const keyColumns = new Set(args.keyColumns);
  const eligible: Array<{ column: string; cardinality: number }> = [];
  const skippedColumns: SkippedColumn[] = [];

  args.columns.forEach((column, index) => {
    if (keyColumns.has(column)) {
      skippedColumns.push({ column, reason: "key-column" });
      return;
    }
    let hasNulls = false;
    const values = new Set<string>();
    for (const row of args.rows) {
      const value = row[index];
      if (value === null || value === undefined) {
        hasNulls = true;
        break;
      }
      values.add(String(value));
    }
    if (hasNulls) {
      skippedColumns.push({ column, reason: "has-nulls" });
    } else if (args.rows.length > 1 && values.size === 1) {
      skippedColumns.push({ column, reason: "constant" });
    } else {
      eligible.push({ column, cardinality: values.size });
    }
  });

  if (eligible.length <= MAX_DISCOVERY_COLUMNS) {
    return { pool: eligible.map((entry) => entry.column), skippedColumns };
  }

  // Over the cap, keep the highest-cardinality columns (schema order breaks
  // ties, so the choice is deterministic) — but the pool itself stays in
  // schema order so emitted tuples read like the schema does.
  const kept = new Set(
    [...eligible]
      .sort((a, b) => b.cardinality - a.cardinality)
      .slice(0, MAX_DISCOVERY_COLUMNS)
      .map((entry) => entry.column),
  );
  const pool: string[] = [];
  for (const { column } of eligible) {
    if (kept.has(column)) {
      pool.push(column);
    } else {
      skippedColumns.push({ column, reason: "over-column-cap" });
    }
  }
  return { pool, skippedColumns };
}

/** True when every row has a distinct, fully non-null value combination. */
function tupleIsUnique(
  rows: ReadonlyArray<readonly unknown[]>,
  indexes: readonly number[],
): boolean {
  const seen = new Set<string>();
  for (const row of rows) {
    const parts: string[] = [];
    for (const index of indexes) {
      const value = row[index];
      // A null key part makes the row unreachable by lookup, so the tuple
      // cannot serve as a unique index.
      if (value === null || value === undefined) return false;
      parts.push(String(value));
    }
    const key = buildCompositeKey(parts);
    if (seen.has(key)) return false;
    seen.add(key);
  }
  return true;
}

/** Order-insensitive identity for a tuple. */
function tupleKey(tuple: readonly string[]): string {
  return buildCompositeKey([...tuple].sort());
}

function containsKnownUnique(
  knownUnique: ReadonlyArray<ReadonlySet<string>>,
  combo: readonly string[],
): boolean {
  return knownUnique.some(
    (unique) =>
      unique.size <= combo.length &&
      combo.filter((column) => unique.has(column)).length === unique.size,
  );
}

/** All size-`k` combinations of `items`, preserving input order. */
function* combinations(
  items: readonly string[],
  size: number,
): Generator<string[]> {
  const combo: string[] = [];
  function* walk(start: number): Generator<string[]> {
    if (combo.length === size) {
      yield [...combo];
      return;
    }
    // Not enough items left to fill the combination.
    for (let i = start; i <= items.length - (size - combo.length); i += 1) {
      combo.push(items[i]);
      yield* walk(i + 1);
      combo.pop();
    }
  }
  yield* walk(0);
}
